import { useCallback, useEffect, useState } from 'react';
import { useStore } from '../store/store';
import { useOverlay, leftShownStatus } from '../contexts/OverlayContext';
import { scrollToItem } from '../components/Middle/InfinitePage';
import { isPreviewMode } from './undoHistory';
import { matchUndoRedoKey } from './undoKeys';
import { isAWritableBox } from '../utils/boxes';
import { restoreHolder } from '../collab/structureLocks';

// Pendant l'édition d'une box à éditeur DraftJS (texte, titre, tableau) ou d'une
// variable au formulaire, Cmd+Z doit rester l'undo natif de DraftJS → le
// raccourci clavier ne l'intercepte pas. Mais sélectionner une box SANS éditeur
// (image, forme, trait, section…) ne doit pas désarmer le raccourci : il n'y a
// pas d'undo natif à préserver. On ne touche jamais la sélection.
const isEditingBox = (s) => {
  if (s.boxVariableSelectedId) return true;
  const box = s.boxes.find((b) => b.id === s.selectedBoxId);
  return !!box && isAWritableBox(box.type);
};

// Hook du do/undo UI : boutons + raccourcis clavier + navigation (scroll) vers
// le changement annulé (potentiellement dans un autre mode : masque, formulaire).
// Champ de saisie classique (variable texte du Formulaire...) : son undo est
// celui du navigateur.
const isNativeField = (el) => el?.tagName === 'TEXTAREA' || el?.tagName === 'INPUT';
const isTextField = (el) => !!el?.isContentEditable || isNativeField(el);

// Les boutons font exactement ce que fait le raccourci (ED-864) : ils envoient
// Cmd/Ctrl+Z à l'élément qui a le focus — NavbarButton annule le mousedown,
// l'éditeur le garde. En plein texte, Draft annule la frappe ; ailleurs,
// l'écouteur clavier ci-dessous annule la dernière action du document.
// keyCode : Draft le lit (getDefaultKeyBinding), le constructeur ne le pose
// pas. Les deux modificateurs : Draft attend Cmd sur Mac, Ctrl ailleurs.
// Un raccourci simulé ne déclenche pas l'undo du navigateur : dans un champ
// de saisie, si personne ne l'a pris en charge (preventDefault), on le
// demande au navigateur, comme le ferait le vrai Cmd+Z.
const sendUndoRedoKey = (shiftKey) => {
  const event = new KeyboardEvent('keydown', {
    key: 'z',
    code: 'KeyZ',
    metaKey: true,
    ctrlKey: true,
    shiftKey,
    bubbles: true,
    cancelable: true,
  });
  Object.defineProperty(event, 'keyCode', { get: () => 90 });
  Object.defineProperty(event, 'which', { get: () => 90 });
  const target = document.activeElement ?? document.body;
  const handled = !target.dispatchEvent(event);
  if (!handled && isNativeField(target)) {
    document.execCommand(shiftKey ? 'redo' : 'undo');
  }
};

// Vrai tant qu'un texte (éditeur Draft ou champ de saisie) a le focus : son
// historique propre, invisible d'ici, peut avoir quelque chose à annuler.
const useTextFieldFocus = () => {
  const [focused, setFocused] = useState(() => isTextField(document.activeElement));
  useEffect(() => {
    const update = () => setFocused(isTextField(document.activeElement));
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
    };
  }, []);
  return focused;
};

export const useUndoRedo = () => {
  const { overlayProps, updateLeftShownStatus } = useOverlay();

  // Texte au focus : son historique propre peut avoir quelque chose à annuler,
  // les boutons restent actifs, comme le raccourci.
  const textFieldFocused = useTextFieldFocus();
  const isPreview = useStore(isPreviewMode);
  const hasUndo = useStore((s) => s.undoStack.length > 0);
  const hasRedo = useStore((s) => s.redoStack.length > 0);
  const canUndo = (hasUndo || textFieldFocused) && !isPreview;
  const canRedo = (hasRedo || textFieldFocused) && !isPreview;

  // Amène l'utilisateur là où le changement a eu lieu, en quittant d'abord le
  // mode courant si besoin (cross-mode). Ne sélectionne JAMAIS de box : on
  // scrolle seulement si elle n'est pas déjà visible.
  const navigateToFocus = useCallback(
    (focus) => {
      if (!focus) return;
      const s = useStore.getState();

      if (focus.type === 'mask') {
        s.setMaskEditing([focus.id]);
        return;
      }

      if (focus.type === 'variable') {
        updateLeftShownStatus(leftShownStatus.VARIABLES);
        return;
      }

      // Box éditée depuis la vue Variables (formulaire) : rouvrir ce panneau et
      // scroller sur la box dans le formulaire (boxId → handleBoxVariableSelectedId),
      // au lieu de revenir à la page infinie. Flag posé à la capture.
      if (focus.fromVariablesView) {
        updateLeftShownStatus(leftShownStatus.VARIABLES, focus.id);
        return;
      }

      // box ou container : revenir à la page infinie, puis scroller.
      if (s.maskEditing?.length > 0) s.setMaskEditing([]);
      if (overlayProps.left.shownStatus === leftShownStatus.VARIABLES) {
        updateLeftShownStatus(leftShownStatus.SUMMARY);
      }

      const box =
        focus.type === 'box'
          ? s.boxes.find((b) => b.id === focus.id)
          : s.boxes.find((b) => {
              const c = s.containers.find((ct) => ct.id === focus.id);
              return c?.columnsIds?.includes(b.columnId);
            });
      if (!box) return;
      requestAnimationFrame(() => scrollToItem(box, { onlyIfNotVisible: true }));
    },
    [overlayProps, updateLeftShownStatus]
  );

  // Pendant une restauration de version (la sienne ou celle d'un co-éditeur,
  // Cmd+Z passe sous l'overlay) : un undo appliquerait des inverses de l'état
  // pré-restore. La pile est vidée à la fin du restore de toute façon.
  const isRestoreFrozen = (s) =>
    Boolean(s.isRestoringVersion) || Boolean(restoreHolder(s));

  const triggerUndo = useCallback(() => {
    const s = useStore.getState();
    if (isPreviewMode(s) || isRestoreFrozen(s)) return;
    navigateToFocus(s.undo());
  }, [navigateToFocus]);

  const triggerRedo = useCallback(() => {
    const s = useStore.getState();
    if (isPreviewMode(s) || isRestoreFrozen(s)) return;
    navigateToFocus(s.redo());
  }, [navigateToFocus]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const action = matchUndoRedoKey(e);
      if (!action) return;
      const s = useStore.getState();
      // En édition de box, aperçu, ou pile vide : on NE preventDefault PAS →
      // l'undo natif de DraftJS reste disponible pendant la frappe.
      if (isPreviewMode(s) || isEditingBox(s)) return;
      // Frappe dans un texte qui n'est pas celui d'une box texte (forme
      // composée, carte info, volet…) : l'undo natif de Draft, pas celui du
      // document.
      if (e.target?.isContentEditable) return;
      const can =
        action === 'undo' ? s.undoStack.length > 0 : s.redoStack.length > 0;
      if (!can) return;
      e.preventDefault();
      if (action === 'undo') triggerUndo();
      else triggerRedo();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [triggerUndo, triggerRedo]);

  const pressUndo = useCallback(() => sendUndoRedoKey(false), []);
  const pressRedo = useCallback(() => sendUndoRedoKey(true), []);

  return { canUndo, canRedo, pressUndo, pressRedo };
};
